import { useEffect } from 'react';
import { LanguageProvider, useLanguageContext } from './i18n/language-context';
import './i18n/i18n';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'components/ui/toaster';
import { ClientMiddleware } from 'state/client-middleware';
import MainLayout from 'pages/main/main-layout';
import { AuthLayout } from './pages/auth/auth-layout';
import { SigninPage } from 'pages/auth/signin/signin-page';
import { SignupPage } from 'pages/auth/signup/signup-page';
import { EmailVerification } from 'pages/auth/email-verification/email-verification';
import { SetPasswordPage } from './pages/auth/set-password/set-password';
import { ActivationSuccess } from './pages/auth/activation-success/activation-success';
import { VerificationFailed } from './pages/auth/verification-failed/verification-failed';
import { ResetPasswordPage } from './pages/auth/reset-password/reset-password';
import { ForgotPasswordPage } from './pages/auth/forgot-password/forgot-password';
import { IamTablePage } from './pages/main/iam-table';
import { Profile } from './pages/profile/profile';
import { ThemeProvider } from './components/core/theme-provider';
import { SidebarProvider } from 'components/ui/sidebar';
import { VerifyOtpKey } from './pages/auth/verify-otp-key/verify-otp-key';
import ChatPage from 'pages/chat/chat';
import HomePage from './pages/home/home';
import SignupEmailSentPage from 'pages/auth/signup-email-sent/signup-email-sent';
import { RedirectPage } from './pages/auth/redirect/redirect';
import { LoadingOverlay } from './components/core/loading-overlay';
import { NoAuthMiddleware } from './state/no-auth-middleware';
import UserHolder from 'pages/user-holder';
import { PostAuthMiddleware } from './state/post-auth-missleware';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: 0,
    },
  },
});

function RedirectHandler() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/success') {
      const headers = new Headers();
      headers.set('x-current-path', location.pathname);

      setTimeout(() => {
        window.location.href = '/';
      }, 10000);
    }
  }, [location]);

  return null;
}

function AppContent() {
  const { isLoading } = useLanguageContext();

  if (isLoading) {
    return <LoadingOverlay />;
  }
  return (
    <div className="min-h-screen bg-background font-sans antialiased">
      <RedirectHandler />
      <ClientMiddleware>
        <ThemeProvider>
          <SidebarProvider>
            <Routes>
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<SigninPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/sent-email" element={<EmailVerification />} />
                <Route path="/activate" element={<SetPasswordPage />} />
                <Route path="/resetpassword" element={<ResetPasswordPage />} />
                <Route path="/success" element={<ActivationSuccess />} />
                <Route path="/activate-failed" element={<VerificationFailed />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/verify-key" element={<VerifyOtpKey />} />
                <Route path="/signup-email-sent" element={<SignupEmailSentPage />} />
                <Route path="/redirect" element={<RedirectPage />} />
              </Route>
              <Route element={<PostAuthMiddleware><UserHolder /></PostAuthMiddleware>}>
                <Route element={<MainLayout />}>
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/identity-management" element={<IamTablePage />} />
                  <Route path="/chat" element={<ChatPage />} />
                  <Route path="/chat/:conversationId" element={<ChatPage />} />
                </Route>

                <Route path="/home" element={<HomePage />} />
                <Route path="/" element={<Navigate to="/home" />} />
              </Route>
            </Routes>
          </SidebarProvider>
        </ThemeProvider>
      </ClientMiddleware>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <NoAuthMiddleware>
          <LanguageProvider defaultLanguage="en-US" defaultModules={['common', 'auth']}>
            <AppContent />
          </LanguageProvider>
        </NoAuthMiddleware>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
